import React, { useState, useEffect } from 'react';
import { supportAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './SupportTickets.css';

function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    userRole: ''
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTickets();
    fetchStatistics();
  }, [filters, currentPage]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      const response = await supportAPI.getAllTickets(params);
      setTickets(response.data.data.tickets);
      setTotalPages(response.data.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      alert('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await supportAPI.getStatistics();
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await supportAPI.getTicketById(ticketId);
      setSelectedTicket(response.data.data.ticket);
      setResponseText('');
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      alert('Failed to load ticket details');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await supportAPI.updateTicketStatus(ticketId, { status: newStatus });
      alert('Ticket status updated successfully');
      fetchTickets();
      if (selectedTicket && selectedTicket.ticketId === ticketId) {
        handleViewTicket(ticketId);
      }
    } catch (error) {
      console.error('Failed to update ticket status:', error);
      alert('Failed to update ticket status');
    }
  };

  const handleAddResponse = async () => {
    if (!responseText.trim()) {
      alert('Please enter a response');
      return;
    }

    try {
      await supportAPI.addResponse(selectedTicket.ticketId, {
        message: responseText
      });
      alert('Response added successfully');
      setResponseText('');
      handleViewTicket(selectedTicket.ticketId);
    } catch (error) {
      console.error('Failed to add response:', error);
      alert('Failed to add response');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
      in_progress: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      resolved: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      closed: 'linear-gradient(135deg, #666 0%, #888 100%)'
    };
    return colors[status] || 'linear-gradient(135deg, #666 0%, #888 100%)';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#51cf66',
      medium: '#ffa500',
      high: '#ff6b6b',
      urgent: '#cc0000'
    };
    return colors[priority] || '#666';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-support-tickets">
      <Navbar />
      <div className="support-container">
        <div className="support-header">
          <h1>Support Ticket Management</h1>
          <button className="btn-refresh" onClick={() => { fetchTickets(); fetchStatistics(); }}>
            Refresh
          </button>
        </div>

        {statistics && (
          <div className="statistics-panel">
            <div className="stat-card" style={{ background: getStatusColor('open') }}>
              <div className="stat-value">{statistics.byStatus.open || 0}</div>
              <div className="stat-label">Open</div>
            </div>
            <div className="stat-card" style={{ background: getStatusColor('in_progress') }}>
              <div className="stat-value">{statistics.byStatus.in_progress || 0}</div>
              <div className="stat-label">In Progress</div>
            </div>
            <div className="stat-card" style={{ background: getStatusColor('resolved') }}>
              <div className="stat-value">{statistics.byStatus.resolved || 0}</div>
              <div className="stat-label">Resolved</div>
            </div>
            <div className="stat-card" style={{ background: getStatusColor('closed') }}>
              <div className="stat-value">{statistics.byStatus.closed || 0}</div>
              <div className="stat-label">Closed</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="stat-value">{statistics.total || 0}</div>
              <div className="stat-label">Total Tickets</div>
            </div>
          </div>
        )}

        <div className="filters-section">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All Categories</option>
            <option value="order_issue">Order Issue</option>
            <option value="payment_issue">Payment Issue</option>
            <option value="delivery_issue">Delivery Issue</option>
            <option value="account_issue">Account Issue</option>
            <option value="technical_issue">Technical Issue</option>
            <option value="feedback">Feedback</option>
            <option value="other">Other</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <select
            value={filters.userRole}
            onChange={(e) => setFilters({ ...filters, userRole: e.target.value })}
          >
            <option value="">All User Types</option>
            <option value="user">Customer</option>
            <option value="restaurant">Restaurant</option>
            <option value="delivery">Delivery Partner</option>
            <option value="business">Business (QuickWinc)</option>
          </select>
        </div>

        <div className="tickets-list">
          {loading ? (
            <div className="loading">Loading tickets...</div>
          ) : tickets.length === 0 ? (
            <div className="no-tickets">No tickets found</div>
          ) : (
            tickets.map(ticket => (
              <div key={ticket._id} className="ticket-card" onClick={() => handleViewTicket(ticket.ticketId)}>
                <div className="ticket-header-row">
                  <div className="ticket-id">#{ticket.ticketId}</div>
                  <div className="ticket-status" style={{ background: getStatusColor(ticket.status) }}>
                    {ticket.status.replace('_', ' ').toUpperCase()}
                  </div>
                  <div className="ticket-priority" style={{ background: getPriorityColor(ticket.priority) }}>
                    {ticket.priority.toUpperCase()}
                  </div>
                </div>
                <div className="ticket-subject">{ticket.subject}</div>
                <div className="ticket-meta">
                  <span className="ticket-user">
                    {ticket.userRole.toUpperCase()}: {ticket.userName}
                  </span>
                  <span className="ticket-category">
                    {ticket.category.replace('_', ' ')}
                  </span>
                  <span className="ticket-date">
                    {formatDate(ticket.createdAt)}
                  </span>
                </div>
                {ticket.responses && ticket.responses.length > 0 && (
                  <div className="ticket-responses-count">
                    {ticket.responses.length} response{ticket.responses.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

        {selectedTicket && (
          <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Ticket #{selectedTicket.ticketId}</h2>
                <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
              </div>

              <div className="modal-body">
                <div className="ticket-details">
                  <div className="detail-row">
                    <strong>Subject:</strong>
                    <span>{selectedTicket.subject}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Status:</strong>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.ticketId, e.target.value)}
                      className="status-select"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className="detail-row">
                    <strong>Priority:</strong>
                    <span style={{ color: getPriorityColor(selectedTicket.priority) }}>
                      {selectedTicket.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-row">
                    <strong>Category:</strong>
                    <span>{selectedTicket.category.replace('_', ' ')}</span>
                  </div>
                  <div className="detail-row">
                    <strong>User:</strong>
                    <span>{selectedTicket.userName} ({selectedTicket.userRole})</span>
                  </div>
                  <div className="detail-row">
                    <strong>Email:</strong>
                    <span>{selectedTicket.userEmail}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Created:</strong>
                    <span>{formatDate(selectedTicket.createdAt)}</span>
                  </div>
                  <div className="detail-row full-width">
                    <strong>Description:</strong>
                    <p>{selectedTicket.description}</p>
                  </div>
                </div>

                <div className="responses-section">
                  <h3>Conversation History</h3>
                  <div className="responses-list">
                    {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                      selectedTicket.responses.map((response, index) => (
                        <div key={index} className={`response-item ${response.responderRole === 'admin' ? 'admin-response' : 'user-response'}`}>
                          <div className="response-header">
                            <strong>{response.responderName}</strong>
                            <span className="response-role">({response.responderRole})</span>
                            <span className="response-time">{formatDate(response.timestamp)}</span>
                          </div>
                          <div className="response-message">{response.message}</div>
                        </div>
                      ))
                    ) : (
                      <div className="no-responses">No responses yet</div>
                    )}
                  </div>

                  <div className="add-response">
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Type your response here..."
                      rows="4"
                    />
                    <button onClick={handleAddResponse} className="btn-send">
                      Send Response
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSupportTickets;
